import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable()
export class RequestQueueService {

    private syncQueue: Queue;
    private asyncQueues: any;
    private cachedRequests: any;
    
    constructor() {
        this.syncQueue = new Queue();
        this.asyncQueues = {};
        this.cachedRequests = {};
    }

    public queueRequest(requestFunc: (url: string, data?: any, elements?: string) => Observable<any>, requestArgs: QueueRequestArgs, cacheName: string = null): number {
        let item = new QueueItem(requestFunc, requestArgs);

        // Si no se indica una llave
        if (!cacheName)
            item.cacheName = item.getRequestId().toString();
        else {
            item.cacheName = cacheName;

            // si la llave se indica pero no esta cachada se crea
            if (!this.cachedRequests[cacheName])
                this.cachedRequests[cacheName] = item;
            else {
                // si la llave ya existe, agrega el nuevo request como hijo y sus post-acciones sincronicas a las del padre
                item.parentRequest = this.cachedRequests[cacheName];
                item.parentRequest.callbacks.push(requestArgs.onSuccess);
                item.parentRequest.fallbacks.push(requestArgs.onFail);
            }
        }
        
        if (requestArgs.isAsync) {
            // agrupa los requests que tengan la misma llave
            let queue = this.getAsyncQueue(item.cacheName);
            queue.push(item);
            this.checkQueue(queue);
        } else {
            // concatena los requests
            this.syncQueue.push(item);
            this.checkQueue(this.syncQueue);
        }

        return item.getRequestId();
    }

    private getAsyncQueue(key: string): Queue {
        let queue: Queue;

        if (this.asyncQueues[key])
            queue = this.asyncQueues[key];
        else {
            queue = new Queue();
            this.asyncQueues[key] = queue;
        }

        return queue;
    }

    private checkQueue(queue: Queue, forceNext: boolean = false): void {
        if (!queue.isProcessing || forceNext)
            this.fireRequest(queue, queue.pop());
    }

    private fireRequest(source: Queue, item: QueueItem): void {
        if (!item)
            return;

        if (item.isCompleted || (item.parentRequest && item.parentRequest.isCompleted)) {
            this.checkQueue(source, true);
            return;
        }

        source.isProcessing = true;

        item.requestFunc(item.requestArgs.requestUrl, item.requestArgs.postData, item.requestArgs.source)
            .subscribe(
                response => {
                    for(let callback of item.callbacks) {
                        if (callback)
                            callback(response);
                    }

                    this.endRequest(source, item);
                },
                error => {
                    // this.errorService.processException(error) Have to throw error

                    for(let fallback of item.fallbacks) {
                        if (fallback)
                            fallback(error);
                    }

                    this.endRequest(source, item);
                }
            );
    }

    private endRequest(source: Queue, item: QueueItem): void {
        item.isCompleted = true;
        source.isProcessing = false;

        if (item.cacheName && this.cachedRequests[item.cacheName])
            delete this.cachedRequests[item.cacheName];

        if (item.requestArgs.isAsync && item.cacheName && this.asyncQueues[item.cacheName])
            delete this.asyncQueues[item.cacheName];

        this.checkQueue(source, true);
    }
}

export class QueueRequestArgs {
    public requestUrl: string;
    public postData: any;
    public source: string;
    public isAsync: boolean;
    public onSuccess: (response: any) => void;
    public onFail: (error: any) => void;
}

class Queue {
    private pending: QueueItem[] = [];
    public isProcessing: boolean = false;

    getHasItems(): boolean { return this.pending.length > 0; }
    getCurrent(): QueueItem { return this.pending[0]; }

    push(item: QueueItem): void { this.pending.push(item); }
    pop(): QueueItem {
        if (!this.getHasItems()) 
            return null;

        let item = this.pending[0]; 
        this.pending.splice(0, 1);

        return item;
    }
}

class QueueItem {
    private requestId: number = Math.round(Math.random() * 10000);

    public parentRequest: QueueItem;
    public requestFunc: (url: string, data: any, elements: string) => Observable<any>;
    public requestArgs: any;
    public callbacks: ((response: any) => void)[] = [];
    public fallbacks: ((error: Error) => void)[] = [];
    public isCompleted: boolean = false;
    public cacheName: string;

    constructor(mainFunc: (url: string, data: any, elements: string) => Observable<any>, requestArgs: QueueRequestArgs) {
        this.requestFunc = mainFunc;
        this.requestArgs = requestArgs;

        if (requestArgs.onSuccess)
            this.callbacks.push(requestArgs.onSuccess);
        
        if (requestArgs.onFail)
            this.fallbacks.push(requestArgs.onFail);
    }

    getRequestId(): number { return this. requestId; }
}